<script setup>
import { computed } from 'vue'
import LazyLoad from './../directives/LazyLoad'
import { useStore } from '@composables/use-store'
import { useQueueService } from '@composables/use-queue-service'

const vLazyload = LazyLoad

const props = defineProps({
    resource: {
        type: [Object, Boolean],
        default: false,
    },
    type: {
        type: String,
        default: 'album_art',
    },
    width: {
        type: [Boolean, Number],
        default: false,
    },
    height: {
        type: [Boolean, Number],
        default: false,
    },
    fill: {
        type: Boolean,
        default: true,
    },
    centered: {
        type: Boolean,
        default: false,
    },
    playableMix: {
        type: [Object, Boolean],
        default: false,
    },
    hasHoverState: {
        type: Boolean,
        default: false,
    },
    isBackground: {
        type: Boolean,
        default: false,
    },
    playlistId: {
        type: Number,
        default: null,
    },
    isPlaylistMix: {
        type: Boolean,
        default: false,
    },
    queueIndex: {
        type: [Number, Boolean],
        default: false,
    },
    shouldLazyload: {
        type: Boolean,
        default: true,
    },
    radius: {
        type: String,
        default: 'none',
        validator: value => ['none', 'list', 'card'].includes(value),
    },
})

const store = useStore()
const queueService = useQueueService()

const hasArt = computed(() => props.resource && props.resource[props.type] && props.resource[props.type].url)

const isMixLoaded = computed(
    () => store.state.playback.resource && props.playableMix.id === store.state.playback.resource.id
)

const playbackStatus = computed(
    () => store.state.playback.status === 'playing' || store.state.playback.status === 'loading'
)

const isPlaylistLoaded = computed(() => !!props.playlistId && store.state.queue.playlistId === props.playlistId)

const isQueueMix = computed(() => props.queueIndex !== false && props.queueIndex >= 0)

function playMix() {
    if (!props.isPlaylistMix && isMixLoaded.value) {
        return queueService.pause()
    }
    props.queueIndex && queueService.setQueuePosition(props.queueIndex)
    return (
        !props.playlistId &&
        !props.isPlaylistMix &&
        queueService.playMix(props.playableMix, 0, !isQueueMix.value).catch(e => {
            console.error(e)
            store.dispatch('appearance/handleNotification', { message: e.message, type: 'warning' })
        })
    )
}
</script>

<template>
    <div>
        <div
            :class="[
                'resource-image',
                (isMixLoaded || isPlaylistLoaded) && playbackStatus && 'resource-image-playing',
                { 'resource-image-hover': hasHoverState },
                { 'resource-image-fill': fill },
                { 'resource-image-centered': centered },
                { 'resource-image-radius-list': radius === 'list' },
                { 'resource-image-radius-card': radius === 'card' },
            ]"
        >
            <div
                v-if="(playableMix && playableMix.title) || playlistId"
                class="resource-image-icon-container"
                @click="playMix"
            >
                <BaseIcon
                    v-if="(isMixLoaded || isPlaylistLoaded) && playbackStatus"
                    class="resource-image-icon-play"
                    name="pause"
                />
                <BaseIcon
                    v-else
                    class="resource-image-icon-play"
                    name="play_arrow"
                />

                <svg
                    class="resource-image-icon-circle"
                    viewBox="0 0 50 50"
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                >
                    <circle
                        cx="25"
                        cy="25"
                        r="23"
                        stroke="white"
                        stroke-width="3px"
                        fill="none"
                    />
                </svg>
            </div>
            <div
                v-lazyload="shouldLazyload"
                :class="{ lazy: !isBackground && shouldLazyload }"
            >
                <img
                    v-if="hasArt && !isBackground"
                    :width="width"
                    :height="height"
                    :data-src="resource[type].url"
                    :src="!shouldLazyload ? resource[type].url : ''"
                    :alt="`${resource.title || resource.heading} art`"
                    :class="[
                        'resource-image-img',
                        { 'resource-image-img-fill': fill },
                        { 'resource-image-img-centered': centered },
                        'realImage',
                    ]"
                >
            </div>
            <div
                v-if="hasArt && isBackground"
                :class="['resource-image-img', 'resource-image-img-background']"
                :style="{ backgroundImage: `url(${resource[type].url})` }"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.resource-image {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &-fill {
        padding-bottom: 100%;
        background: $c-classics-channel-grad;
    }

    &-centered {
        height: 100%;
        background: $c-classics-channel-grad;
    }

    &-radius-list {
        border-radius: $size-radius-artwork-sm;
    }

    &-radius-card {
        border-radius: $size-radius-artwork;
    }

    &-playing,
    &.resource-image-hover,
    &:hover {
        .resource-image-icon-container {
            opacity: 1;
        }
    }

    &-icon-container {
        @include fill;

        z-index: $layer-highlight-z-index;
        text-align: center;
        cursor: pointer;
        background: $c-frisky-channel-grad-transparent;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
    }

    &-icon-circle {
        @include center;
        @include square(78px);

        max-width: 90%;
        max-height: 90%;
    }

    &-icon-play {
        @include center;
        @include square(67%);

        z-index: $layer-tooltip-z-index;
        max-width: 36px;
        max-height: 36px;
        border-radius: 50%;
        fill: $c-white;
    }

    &-img {
        display: inline-block;
        max-width: 100%;
        object-fit: cover;

        &-fill {
            @include fill;
        }

        &-centered {
            min-width: 100%;
            min-height: 100%;
            object-fit: cover;

            @include center;
        }

        &-background {
            width: 100%;
            height: 100%;
            background-position: center;
            background-size: cover;
        }
    }
}
</style>

<style lang="scss">
.lazy {
    opacity: 0;
    transition: opacity 50ms ease-in-out;
    &.loaded {
        opacity: 1;
    }
}
</style>
